import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import Stack from '@mui/material/Stack';
import MuiTextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { countries } from 'utils/countries';
import { formatDate } from 'utils/dates';

import { roles } from 'server/context/roles';

import ErrorAlert from 'components/ErrorAlert';

import DynamicPEPFields from 'web/form/DynamicPEPFields';
import { TextField, Switch, Select, AutoComplete } from 'web/form/Form';

import { PAGINATE_DEPARTMENTS } from 'api/queries/departmentQueries';
import { PAGINATE_AUDITING_COMPANIES } from 'api/queries/auditingCompanyQueries';

export function NameField() {
	return (
		<TextField name="name" type="text" label="Navn" required fullWidth />
	);
}

export function EmailField({ name = 'email' }) {
	return (
		<TextField name={name} type="email" label="E-post" required fullWidth />
	);
}

export function PhoneField({ name = 'phone' }) {
	return (
		<TextField
			name={name}
			type="tel"
			label="Telefon"
			pattern="^[\d\s+()]{8,}$"
			required
			fullWidth
		/>
	);
}

export function PositionField() {
	return <TextField name="position" type="text" label="Stilling" />;
}

// Omit withheld fields from form state to prevent empty submissions.
function WithheldNorwegianNumberField({ label }) {
	return (
		<MuiTextField
			label={label}
			variant="outlined"
			disabled
			helperText={`${label} er skjult for interne brukere`}
		/>
	);
}

function MaskedNorwegianNumberField({ name, label, onReveal, withheld }) {
	const value = useWatch({ name });
	const { dirtyFields } = useFormState({ name });

	const isStoredNumber = Boolean(value) && !dirtyFields[name];

	const [isRevealed, setIsRevealed] = useState(null);

	const isVisible = isRevealed ?? !isStoredNumber;

	function toggleVisibility() {
		if (!isVisible) {
			onReveal?.(name);
		}

		setIsRevealed(!isVisible);
	}

	if (withheld) {
		return <WithheldNorwegianNumberField label={label} />;
	}

	return (
		<TextField
			name={name}
			type={isVisible ? 'text' : 'password'}
			label={label}
			autoComplete="off"
			// Keep unsupported pseudo-elements in separate selectors to preserve the others.
			className="[&_input::-ms-clear]:hidden [&_input::-ms-reveal]:hidden [&_input::-webkit-credentials-auto-fill-button]:hidden"
			suffix={
				<IconButton
					onClick={toggleVisibility}
					edge="end"
					aria-label={`${isVisible ? 'Skjul' : 'Vis'} ${label.toLowerCase()}`}
					title={isVisible ? 'Skjul' : 'Vis'}
				>
					{isVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
				</IconButton>
			}
		/>
	);
}

export function SocialNumberField({ onReveal, withheld = false }) {
	return (
		<MaskedNorwegianNumberField
			name="socialno"
			label="Personnummer"
			onReveal={onReveal}
			withheld={withheld}
		/>
	);
}

export function DNumberField({ onReveal, withheld = false }) {
	return (
		<MaskedNorwegianNumberField
			name="dNumber"
			label="D-nummer"
			onReveal={onReveal}
			withheld={withheld}
		/>
	);
}

export function AddressFields() {
	return (
		<>
			<TextField name="street_address" label="Gateadresse" fullWidth />

			<Stack spacing={2} direction="row">
				<TextField name="postal_code" label="Postnummer" fullWidth />

				<TextField name="locality" label="Sted" fullWidth />
			</Stack>
		</>
	);
}

export function CountryAutocomplete() {
	return (
		<AutoComplete
			label="Bosatt i land"
			name="country"
			options={countries.map(country => ({
				label: country,
				value: country,
			}))}
		/>
	);
}

export function CitizenshipAutocomplete() {
	return (
		<AutoComplete
			label="Statsborger i land"
			name="citizenship"
			options={countries.map(country => ({
				label: country,
				value: country,
			}))}
		/>
	);
}

export function PepFields({ answer = null }) {
	const { watch } = useFormContext();
	const showPepRolesFields = watch('isPoliticallyExposedPerson');

	return (
		<div>
			<Switch
				name="isPoliticallyExposedPerson"
				label="Ja, jeg er en politisk eksponert person"
				formGroupProps={{ sx: { display: 'inline-block' } }}
			/>

			{answer && <PepAnswer answer={answer} />}

			{showPepRolesFields && (
				<DynamicPEPFields name="userPoliticallyExposedPersonRoles" />
			)}
		</div>
	);
}

function PepAnswer({ answer }) {
	if (answer.answered) {
		return (
			<p className="m-0 text-sm text-text-muted">
				PEP-bekreftelse fra brukeren er registrert
				{answer.answeredAt &&
					` (${formatDate(answer.answeredAt, 'DD.MM.YYYY')})`}
				.
			</p>
		);
	}

	return (
		<p className="m-0 text-sm font-semibold text-danger-strong">
			Ingen PEP-bekreftelse fra brukeren er registrert.
			{answer.willBeAsked && ' Spørsmålet kommer ved neste innlogging.'}
		</p>
	);
}

export function UserTypeSelect({ hideInEditMode = false, ...props }) {
	if (hideInEditMode) {
		return null;
	}

	return (
		<Select
			label="Brukertype"
			name="role"
			options={roles.map(r => ({
				value: r._id,
				label: r.name,
			}))}
			fullWidth
			{...props}
		/>
	);
}

export function DepartmentsAutocomplete({
	currentUser,
	editingUserId,
	...props
}) {
	const { watch } = useFormContext();
	const isUserDepartmentAdmin = watch('role') === 'departmentAdmin';

	const {
		data: { departments = { count: 0, items: [] } } = {
			categories: { count: 0, items: [] },
		},
		loading,
		error,
	} = useQuery(PAGINATE_DEPARTMENTS, {
		skip: !isUserDepartmentAdmin,
	});

	if (!isUserDepartmentAdmin) return null;

	if (error) {
		return <ErrorAlert error={error} />;
	}

	const canEditDepartments =
		currentUser?.role === 'admin' ||
		(currentUser?.role === 'departmentAdmin' &&
			currentUser?._id !== editingUserId);

	return (
		<AutoComplete
			label="Avdelinger"
			name="departmentIds"
			loading={loading}
			disabled={loading || !canEditDepartments || props.disabled}
			options={departments?.items?.map(department => ({
				value: department._id,
				label: department.name,
			}))}
			multiple
			helperText={
				!canEditDepartments
					? 'Du har ikke tilgang til å endre avdelinger på denne brukeren'
					: undefined
			}
			{...props}
		/>
	);
}

export function AuditingCompanyAutocomplete() {
	const { watch } = useFormContext();
	const isUserAuditor = watch('role') === 'auditor';

	const {
		data: { auditingCompanies = { count: 0, items: [] } } = {
			auditingCompanies: { count: 0, items: [] },
		},
		loading,
		error,
	} = useQuery(PAGINATE_AUDITING_COMPANIES, {
		skip: !isUserAuditor,
	});

	if (!isUserAuditor) return null;

	if (error) {
		return <ErrorAlert error={error} />;
	}

	return (
		<AutoComplete
			label="Revisorfirma"
			name="auditingCompanyId"
			loading={loading}
			options={auditingCompanies?.items?.map(auditingCompany => ({
				value: auditingCompany._id,
				label: auditingCompany.name,
			}))}
		/>
	);
}
